import { motion } from "framer-motion";
import PageShell, { SectionLabel } from "@/components/PageShell";
import SEO from "@/components/SEO";

const VALUES = [
  {
    t: "Research first",
    d: "We read the paper before we quote the project. Rigor precedes speed.",
  },
  {
    t: "Small teams",
    d: "A senior-heavy pod ships more than an army of specs. We keep pods lean.",
  },
  {
    t: "Ownership",
    d: "We stay on-call for what we build. Handovers include ourselves.",
  },
  {
    t: "Signal over demo",
    d: "If it doesn't hold up in production, it doesn't ship. Even if it looks great.",
  },
];

const TIMELINE = [
    {
    y: "2023",
    t: "Initial Phase",
    d: "Identifying problems, planning the business and setting goals and targets",
  },
  {
    y: "2024",
    t: "Founded",
    d: "Started as a two-person research consultancy focused on applied ML.",
  },
  {
    y: "2025",
    t: "Got Incorporated",
    d: "The company got registered and got legal status from the government of India.",
  },
  {
    y: "2026",
    t: "Operational",
    d: "Serving clients all over the world, developing products and scalling infrastructure.",
  },
];

export default function About() {
  return (
    <PageShell testId="page-about">
      <SEO
        title="About — Imaginepedia Technologies"
        description="Learn about Imaginepedia Technologies, what we do and what we're building."
        path="/about"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-4">
            <SectionLabel index={1}>About us</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              A research studio
              <br />
              <span className="text-zinc-500">disguised as a</span>
              <br />
              software firm.
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              Imaginepedia Technologies is a tech-focused software and AI
              research &amp; development firm. We partner with ambitious teams
              to turn frontier ideas into deployed systems — reliable,
              observable and, when it matters, formally verified.
            </p>
          </div>
        </div>

        {/* Mission */}
        <section className="border-t border-white/5 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <SectionLabel index={2}>Mission</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-3xl md:text-4xl text-white leading-tight">
              To make sure the best ideas in software and AI don&apos;t stay
              stuck in whitepapers — but land in production, in the hands of the
              people they were imagined for.
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="border-t border-white/5 py-24">
          <SectionLabel index={3}>Values</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="bg-[#0C0C0D] border border-white/5 p-8 hover:border-[#CCFF00]/30 transition-colors duration-300"
              >
                <p className="font-mono text-[10px] text-zinc-600 mb-3">
                  0{i + 1}
                </p>
                <h3 className="font-display text-2xl text-white mb-3">{v.t}</h3>
                <p className="text-zinc-400 leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="border-t border-white/5 py-24">
          <SectionLabel index={4}>Timeline</SectionLabel>
          <div className="mt-8 space-y-2">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.y}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-4 py-6 border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200"
              >
                <p className="col-span-3 md:col-span-2 font-mono text-sm text-[#CCFF00]">
                  {t.y}
                </p>
                <p className="col-span-9 md:col-span-3 font-display text-xl text-white">
                  {t.t}
                </p>
                <p className="col-span-12 md:col-span-7 text-zinc-400">{t.d}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </PageShell>
  );
}
